import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Input, Space, Spin, Tag, Typography, message } from 'antd';
import { ExperimentOutlined, ExportOutlined, PictureOutlined } from '@ant-design/icons';
import { get, post } from '../../api/request';
import { API_ENDPOINTS } from '../../api/config';
import type { AIChatTestResultType, AIImageTestResultType, AISettingType } from './types';
import './AIManagement.css';

const { TextArea } = Input;

const DEFAULT_MESSAGE = '你好，请用一句话说明你已成功连接。';
const DEFAULT_IMAGE_PROMPT = '竖版中国网络小说封面，神秘钟表、旧宅、微光、电影感构图，无文字，无水印';
const QUICKROUTER_SITE_URL = 'https://quickrouter.ai/';

const AIConnectivityTestPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [config, setConfig] = useState<AISettingType | null>(null);
  const [messageText, setMessageText] = useState(DEFAULT_MESSAGE);
  const [imagePrompt, setImagePrompt] = useState(DEFAULT_IMAGE_PROMPT);
  const [result, setResult] = useState<AIChatTestResultType | null>(null);
  const [imageTesting, setImageTesting] = useState(false);
  const [imageResult, setImageResult] = useState<AIImageTestResultType | null>(null);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const data = await get<AISettingType>(API_ENDPOINTS.ai_management.config);
      setConfig(data);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '加载AI配置失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const handleTest = async () => {
    try {
      setTesting(true);
      const data = await post<AIChatTestResultType>(API_ENDPOINTS.ai_management.test, {
        message: messageText,
      });
      setResult(data);
      message.success('连通测试成功');
    } catch (error) {
      setResult(null);
      message.error(error instanceof Error ? error.message : '连通测试失败');
    } finally {
      setTesting(false);
    }
  };

  const handleImageTest = async () => {
    try {
      setImageTesting(true);
      const data = await post<AIImageTestResultType>(API_ENDPOINTS.ai_management.testImage, {
        prompt: imagePrompt,
        size: config?.image_size,
      });
      setImageResult(data);
      message.success('生图测试成功');
    } catch (error) {
      setImageResult(null);
      message.error(error instanceof Error ? error.message : '生图测试失败');
    } finally {
      setImageTesting(false);
    }
  };

  const imageSrc = imageResult?.image_url || (imageResult?.b64_json ? `data:image/png;base64,${imageResult.b64_json}` : '');

  return (
    <div className="ai-management-page">
      <h1 className="page-title">原平台AI测试</h1>

      <Card bordered={false} className="ai-section-card">
        <Spin spinning={loading}>
          {config && (
            <Space wrap className="ai-summary">
              <Tag color="blue">
                提供商: {config.provider}
                <a className="ai-provider-link" href={QUICKROUTER_SITE_URL} target="_blank" rel="noreferrer">
                  官网/充值 <ExportOutlined />
                </a>
              </Tag>
              <Tag color="geekblue">写作模型: {config.model}</Tag>
              <Tag color="cyan">生图首选: {config.image_model}</Tag>
              <Tag color="gold">生图兜底: {config.image_fallback_model}</Tag>
              <Tag color="purple">生图尺寸: {config.image_size}</Tag>
              <Tag color={config.has_api_key ? 'green' : 'red'}>
                Key: {config.has_api_key ? '已配置' : '未配置'}
              </Tag>
              <Tag color="purple">Base URL: {config.base_url}</Tag>
            </Space>
          )}

          <Alert
            type="warning"
            showIcon
            message="这个页面只用来验证当前配置是否能正常打到 QuickRouter。"
            description="生图测试会按后台配置先试首选模型，再试兜底和系统内置便宜模型池；返回结果里的 fallback_attempts 可看到失败模型。"
            style={{ marginBottom: 16 }}
          />

          <TextArea
            value={messageText}
            onChange={(event) => setMessageText(event.target.value)}
            rows={4}
            placeholder="输入一条测试消息"
            maxLength={4000}
          />

          <div className="ai-actions">
            <Button type="primary" icon={<ExperimentOutlined />} loading={testing} onClick={handleTest}>
              开始测试
            </Button>
          </div>
        </Spin>
      </Card>

      <Card bordered={false} className="ai-section-card" title="测试结果">
        {result ? (
          <div className="ai-result">
            <Space wrap className="ai-result-meta">
              <Tag color="blue">模型: {result.model || '-'}</Tag>
              <Tag color="green">耗时: {result.elapsed_ms} ms</Tag>
              {result.finish_reason && <Tag color="gold">结束原因: {result.finish_reason}</Tag>}
            </Space>

            <div className="ai-result-block">
              <Typography.Title level={5}>回复内容</Typography.Title>
              <div className="ai-result-text">{result.content || '-'}</div>
            </div>

            {result.reasoning_content ? (
              <div className="ai-result-block">
                <Typography.Title level={5}>推理内容</Typography.Title>
                <div className="ai-result-text">{result.reasoning_content}</div>
              </div>
            ) : null}

            {result.usage ? (
              <div className="ai-result-block">
                <Typography.Title level={5}>Usage</Typography.Title>
                <pre className="ai-result-json">{JSON.stringify(result.usage, null, 2)}</pre>
              </div>
            ) : null}

            <div className="ai-result-block">
              <Typography.Title level={5}>原始返回</Typography.Title>
              <pre className="ai-result-json">{JSON.stringify(result.raw, null, 2)}</pre>
            </div>
          </div>
        ) : (
          <Alert type="info" showIcon message="还没有测试结果，先发一条消息试试。" />
        )}
      </Card>

      <Card bordered={false} className="ai-section-card" title="AI生图测试">
        <Alert
          type="info"
          showIcon
          message="这里会使用 AI配置 中的首选生图模型和兜底模型，真实请求 QuickRouter 图片接口。"
          description="推荐首选可灵，兜底 Gemini；GPT Image 较贵，只建议作为最后兜底。"
          style={{ marginBottom: 16 }}
        />
        <TextArea
          value={imagePrompt}
          onChange={(event) => setImagePrompt(event.target.value)}
          rows={4}
          placeholder="输入一段生图提示词"
          maxLength={4000}
        />
        <div className="ai-actions">
          <Button type="primary" icon={<PictureOutlined />} loading={imageTesting} onClick={handleImageTest}>
            开始生图测试
          </Button>
        </div>
        {imageResult ? (
          <div className="ai-image-result">
            <Space wrap className="ai-result-meta">
              <Tag color="cyan">实际模型: {imageResult.model || config?.image_model || '-'}</Tag>
              <Tag color="green">耗时: {imageResult.elapsed_ms} ms</Tag>
            </Space>
            {imageSrc ? <img src={imageSrc} alt="AI生图测试结果" /> : null}
            <div className="ai-result-block">
              <Typography.Title level={5}>原始返回</Typography.Title>
              <pre className="ai-result-json">{JSON.stringify(imageResult.raw, null, 2)}</pre>
            </div>
          </div>
        ) : (
          <Alert type="info" showIcon message="还没有生图测试结果，输入提示词后点击开始生图测试。" style={{ marginTop: 16 }} />
        )}
      </Card>
    </div>
  );
};

export default AIConnectivityTestPage;
